import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { fetchAdminAuditLogs, AuditLogEntry } from '../../redux/reducers/adminUsersSlice'
import { X, Clock, User, Activity, Globe, Monitor, FileText } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

interface AuditLogModalProps {
  adminId: string
  adminName: string
  onClose: () => void
}

const AuditLogModal: React.FC<AuditLogModalProps> = ({ adminId, adminName, onClose }) => {
  const dispatch = useAppDispatch()
  const { auditLogs, loading } = useAppSelector((state) => state.adminUsers)
  const [filter, setFilter] = useState<string>('all')
  const [page, setPage] = useState(1)

  useEffect(() => {
    // Fetch audit logs when modal opens
    dispatch(fetchAdminAuditLogs({
      adminId,
      params: {
        page,
        limit: 50,
      },
    }))
  }, [dispatch, adminId, page])

  const getActionColor = (action: string) => {
    if (action.includes('create')) return 'text-green-500'
    if (action.includes('update') || action.includes('edit')) return 'text-blue-500'
    if (action.includes('delete') || action.includes('remove')) return 'text-red-500'
    if (action.includes('suspend') || action.includes('lock')) return 'text-orange-500'
    if (action.includes('login')) return 'text-purple-500'
    return 'text-gray-500'
  }

  const getActionIcon = (action: string) => {
    if (action.includes('login') || action.includes('logout')) return User
    if (action.includes('password')) return Clock
    return Activity
  }

  const filteredLogs = filter === 'all' 
    ? auditLogs 
    : auditLogs.filter(log => log.action.toLowerCase().includes(filter.toLowerCase()))

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Audit Logs</h2>
            <p className="text-sm text-muted-foreground mt-1">Activity history for {adminName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                filter === 'all' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              All Actions
            </button>
            <button
              onClick={() => setFilter('login')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                filter === 'login' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              Logins
            </button>
            <button
              onClick={() => setFilter('update')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                filter === 'update' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              Updates
            </button>
            <button
              onClick={() => setFilter('password')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                filter === 'password' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              Password Changes
            </button>
          </div>
        </div>

        {/* Audit Log List */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 200px)' }}>
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="text-muted-foreground mt-2">Loading audit logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No audit logs found</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredLogs.map((log) => {
                const ActionIcon = getActionIcon(log.action)
                return (
                  <div key={log.id} className="px-6 py-4 hover:bg-muted/30 transition-colors">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg bg-muted ${getActionColor(log.action)}`}>
                        <ActionIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              by {log.adminPerformerName || 'System'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(log.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        
                        {/* Details */}
                        {(log.oldValues || log.newValues) && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                            {log.oldValues && (
                              <div className="mb-2">
                                <span className="text-xs text-muted-foreground">Previous: </span>
                                <span className="text-xs font-mono">
                                  {typeof log.oldValues === 'object' 
                                    ? JSON.stringify(log.oldValues, null, 2)
                                    : log.oldValues}
                                </span>
                              </div>
                            )}
                            {log.newValues && (
                              <div>
                                <span className="text-xs text-muted-foreground">New: </span>
                                <span className="text-xs font-mono">
                                  {typeof log.newValues === 'object' 
                                    ? JSON.stringify(log.newValues, null, 2)
                                    : log.newValues}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Metadata */}
                        <div className="mt-2 flex items-center space-x-4 text-xs text-muted-foreground">
                          <div className="flex items-center space-x-1">
                            <Globe className="w-3 h-3" />
                            <span>{log.ipAddress || 'Unknown IP'}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Monitor className="w-3 h-3" />
                            <span className="truncate max-w-xs">
                              {log.userAgent || 'Unknown Device'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer with Pagination */}
        {filteredLogs.length > 0 && (
          <div className="px-6 py-4 border-t border-border flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Showing {filteredLogs.length} audit log entries
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm bg-muted hover:bg-muted/80 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm">Page {page}</span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={filteredLogs.length < 50}
                className="px-3 py-1 text-sm bg-muted hover:bg-muted/80 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AuditLogModal